import { Platform } from '../../platform';
import { View } from '../../primitives/View';
import { CHROME_GLASS_INTENSITY } from '../../styles/glass';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { GlassBackground } from '../Glass';
import { MarqueeLabel } from '../MarqueeLabel';
import {
  BADGE_ICON_ONLY_SIZE_FOR,
  BADGE_LABEL_ICON_SIZE_FOR,
  resolveBadgeIconColor,
} from './Badge.icons';
import type { BadgeProps } from './Badge.types';
import { badgeDotVariants, badgeTextVariants, badgeVariants } from './Badge.variants';

const IS_WEB = Platform.OS === 'web';

/**
 * Cross-platform Badge — a small, non-interactive status / label chip.
 * Renders identically on web and React Native.
 *
 * @example
 * ```tsx
 * import { Badge } from '@flashka/shared/ui';
 * import { Sparkles } from '@flashka/shared/ui/icons';
 *
 * // Label (default type)
 * <Badge colour="success">Active</Badge>
 *
 * // Leading dot — status
 * <Badge type="status" colour="warning">Pending</Badge>
 *
 * // Icon + label
 * <Badge type="icon-label" colour="info" icon={Sparkles}>New</Badge>
 *
 * // Count pill
 * <Badge type="count" colour="error">3</Badge>
 *
 * // Icon-only square
 * <Badge type="icon" colour="primary" icon={Sparkles} accessibilityLabel="Premium" />
 * ```
 */
/**
 * `pinned`'s design boxes — the same 20/24/28/32/36 the `size` axis states as
 * `h-5`…`h-9`, and the same 11/10/12/12/14 type `badgeTextVariants` states as
 * `text-[11px]`/`text-[10px]`/`text-xs`/`text-sm`, but in px so the tablet rem
 * ramp can't move them.
 *
 * `native:`-scoped throughout, exactly like the card title's pin: the ramp is
 * a native-only mechanism (`ResponsiveScaleProvider` swaps NativeWind's `rem`),
 * while on web the rem is how a reader's default font size reaches the chip.
 * There is no ramp to defend against there, so `pinned` is a no-op on web by
 * construction rather than by accident.
 *
 * The TYPE has to be pinned along with the box: these chips are content-sized,
 * so a ramped 15px label inside a pinned 28px pill just made the pill wider —
 * 94pt against the design's 80 — instead of matching it.
 *
 * Written out per size rather than computed so a size added to the axis has to
 * be added here too, instead of silently falling back to a wrong box.
 */
const BADGE_PINNED_BOX = {
  xxs: 'native:h-[20px] native:min-h-[20px]',
  xs: 'native:h-[24px] native:min-h-[24px]',
  s: 'native:h-[28px] native:min-h-[28px]',
  m: 'native:h-[32px] native:min-h-[32px]',
  l: 'native:h-[36px] native:min-h-[36px]',
} as const;
/** `type="icon"` is square — the width has to be pinned with the height. */
const BADGE_PINNED_SQUARE = {
  xxs: 'native:w-[20px]',
  xs: 'native:w-[24px]',
  s: 'native:w-[28px]',
  m: 'native:w-[32px]',
  l: 'native:w-[36px]',
} as const;
/** `type="count"` keeps a circle at one digit via `min-w` = its height. */
const BADGE_PINNED_MIN_W = {
  xxs: 'native:min-w-[20px]',
  xs: 'native:min-w-[24px]',
  s: 'native:min-w-[28px]',
  m: 'native:min-w-[32px]',
  l: 'native:min-w-[36px]',
} as const;
/** Label type per size — 11 (code-side xxs) / L4 10 / L3 12 / L3 12 / L2 14, as px. */
const BADGE_PINNED_TEXT = {
  xxs: 'native:text-[11px]',
  xs: 'native:text-[10px]',
  s: 'native:text-[12px]',
  m: 'native:text-[12px]',
  l: 'native:text-[14px]',
} as const;
/**
 * The rest of the chip's INSIDE, which is content-sized and therefore decides
 * the pill's width: leading pad (a flat 8 at every size), gap (a flat 4), the
 * status dot (6 up to xs, 8 above it) and the trailing pad (12 up to xs, 16
 * above).
 * Stated as the DS's own rules rather than as a copy of the per-(type, size)
 * compound-variant table, so there is no second table to drift.
 *
 * Without these a pinned chip still came out 89pt wide against the design's
 * 80: right height, right type, and every gap around them 25% too big.
 */
const BADGE_PINNED_GAP = 'native:gap-[4px]';
const BADGE_PINNED_LEAD = 'native:pl-[8px]';
const BADGE_PINNED_LABEL_PAD = 'native:px-[8px]';
const BADGE_PINNED_COUNT_PAD = 'native:px-[6px]';
const BADGE_PINNED_TRAIL = {
  xxs: 'native:pr-[12px]',
  xs: 'native:pr-[12px]',
  s: 'native:pr-[16px]',
  m: 'native:pr-[16px]',
  l: 'native:pr-[16px]',
} as const;
/** Status dot — 6 up to xs, 8 from s up (`badgeDotVariants`). */
const BADGE_PINNED_DOT = {
  xxs: 'native:size-[6px]',
  xs: 'native:size-[6px]',
  s: 'native:size-[8px]',
  m: 'native:size-[8px]',
  l: 'native:size-[8px]',
} as const;

export function Badge({
  children,
  colour,
  type,
  size,
  style,
  icon: Icon,
  iconColor: iconColorOverride,
  blur = false,
  pinned = false,
  disabled = false,
  className,
  textClassName,
  accessibilityLabel,
  testID,
  webProps,
  nativeProps,
}: BadgeProps) {
  const { isDark } = useTheme();
  const resolvedColour = colour ?? 'neutral';
  const resolvedType = type ?? 'label';
  const resolvedSize = size ?? 'xs';
  const resolvedStyle = style ?? 'filled';

  const textClass = cn(
    badgeTextVariants({
      colour: resolvedColour,
      size: resolvedSize,
      style: resolvedStyle,
      disabled,
    }),
    // A pinned chip pins its label too, or the content-sized pill grows past
    // the design width anyway (see BADGE_PINNED_TEXT).
    pinned && BADGE_PINNED_TEXT[resolvedSize],
    textClassName,
  );
  // Merge the foreground colour onto the container so web icons inherit it via
  // CSS `currentColor` (Tailwind `dark:` ancestor-scoping then works for the
  // icon too) — same pattern as Button / Chip. The font-size / weight on the
  // container is harmless; the inner <Text> re-applies its own.
  const containerClass = cn(
    badgeVariants({
      colour: resolvedColour,
      type: resolvedType,
      size: resolvedSize,
      style: resolvedStyle,
      disabled,
    }),
    // Frost: web does it with a real backdrop-filter on the chip itself;
    // native stacks the DS `GlassBackground` behind the content. Both need
    // the clip — the blur layer is `inset-0`, so the container's own radius +
    // `overflow-hidden` are what round it, which also means the frost can
    // never drift from the size ramp's radius.
    blur && 'overflow-hidden web:backdrop-blur-sm',
    // Design box, ramp-proof (see `pinned`). Stated after the variants so
    // twMerge drops the rem `h-7` / `w-7` this replaces.
    pinned && BADGE_PINNED_BOX[resolvedSize],
    pinned && resolvedType === 'icon' && BADGE_PINNED_SQUARE[resolvedSize],
    pinned && resolvedType === 'count' && BADGE_PINNED_MIN_W[resolvedSize],
    // The insides too — a content-sized pill is only as wide as these let it
    // be. `icon` has no padding at all (it centres in its square box).
    pinned && resolvedType !== 'icon' && BADGE_PINNED_GAP,
    pinned && resolvedType === 'label' && BADGE_PINNED_LABEL_PAD,
    pinned && resolvedType === 'count' && BADGE_PINNED_COUNT_PAD,
    pinned &&
      (resolvedType === 'icon-label' || resolvedType === 'status') && [
        BADGE_PINNED_LEAD,
        BADGE_PINNED_TRAIL[resolvedSize],
      ],
    textClass,
    className,
  );

  // On web, `currentColor` lets the SVG inherit the container's text colour.
  // On native, `currentColor` isn't honoured by react-native-svg, so resolve
  // the explicit hex.
  const iconColor =
    iconColorOverride ??
    (IS_WEB
      ? 'currentColor'
      : resolveBadgeIconColor(resolvedColour, isDark, resolvedStyle));
  // Figma ramps the icon differently per type: icon-label 12/14/14/16,
  // icon-only 12/14/16/18.
  const iconSize = (
    resolvedType === 'icon' ? BADGE_ICON_ONLY_SIZE_FOR : BADGE_LABEL_ICON_SIZE_FOR
  )[resolvedSize];

  const hasIcon =
    (resolvedType === 'icon-label' || resolvedType === 'icon') && Icon != null;
  const hasDot = resolvedType === 'status';
  const showLabel = resolvedType !== 'icon';

  // Wrap a text-like child (string / number / array of those) in the shared
  // MarqueeLabel (fluid mode) so the colour + typography classes apply AND
  // the label clamps with an ellipsis when the Badge is width-constrained
  // (web adds the hover marquee to read the full text). Unconstrained badges
  // render exactly as before. Pass JSX through untouched.
  // `text-center` on count: the pill's `min-w` can exceed the digit's width
  // (a one-digit circle), and web MarqueeLabel stretches `w-full` across that
  // box — without it the digit hugs the left edge. No-op on native (the box
  // is content-sized there).
  const label =
    showLabel && isTextLike(children) ? (
      <MarqueeLabel
        className={cn(textClass, resolvedType === 'count' && 'text-center')}
        label={children}
      />
    ) : showLabel ? (
      children
    ) : null;

  return (
    <View
      className={containerClass}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      webProps={webProps}
      nativeProps={nativeProps}>
      {/* The chip's own `overflow-hidden` + radius clip it, so no `radius`
          here — the frost can never drift from the size ramp's corner.
          `frostOnAndroid={false}`: a chip only has to lift its label, and it
          sits INSIDE the app's blur target, so it would sample a tree it
          belongs to. */}
      {blur ? (
        <GlassBackground intensity={CHROME_GLASS_INTENSITY} frostOnAndroid={false} />
      ) : null}
      {hasDot ? (
        <View
          className={cn(
            badgeDotVariants({
              colour: resolvedColour,
              size: resolvedSize,
              style: resolvedStyle,
            }),
            pinned && BADGE_PINNED_DOT[resolvedSize],
          )}
        />
      ) : null}
      {/* `shrink-0`: in a width-constrained badge the label (MarqueeLabel)
          absorbs the squeeze — the icon must never be squashed narrower than
          its box (same rule as Button's icons). */}
      {hasIcon && Icon ? (
        <Icon color={iconColor} size={iconSize} className="shrink-0" />
      ) : null}
      {label}
    </View>
  );
}
(Badge as { displayName?: string }).displayName = 'Badge';

function isTextLike(c: unknown): boolean {
  if (c == null || typeof c === 'boolean') return false;
  if (typeof c === 'string' || typeof c === 'number') return true;
  if (Array.isArray(c)) return c.every(isTextLike);
  return false;
}
